"use client";
import {
  Box,
  Collapse,
  Flex,
  HStack,
  Icon,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import Image from "next/image";
import Link from "next/link";
import {BiChevronRight} from "@onemind-services-llc/react-icons-ng/bi";
import {NAV_ITEMS, NavItem, NAV_BUTTON} from "../header.constants";
import {usePathname} from "next/navigation";
import {useEffect, useRef} from "react";

export const MobileNav = ({onClose}: {onClose: () => void}) => {
  const pathname = usePathname();
  const navigationStarted = useRef(false);

  useEffect(() => {
    if (navigationStarted.current) {
      onClose();
      navigationStarted.current = false;
    }
  }, [pathname, onClose]);

  const handleLinkClick = () => {
    navigationStarted.current = true;
  };

  return (
    <HStack>
      <Box
        position="fixed"
        top="93px"
        left={0}
        right={0}
        bg={useColorModeValue("white", "gray.800")}
        display={{md: "none"}}
        w="100%"
        zIndex={9999}
      >
        <Box maxH="calc(100vh - 93px)" overflowY="auto" p={4}>
          {NAV_ITEMS.map((item) => (
            <MobileNavItem key={item.label} {...item} onLinkClick={handleLinkClick} />
          ))}
          <MobileNavItem
            label={NAV_BUTTON.label}
            href={NAV_BUTTON.href}
            onLinkClick={handleLinkClick}
          />
        </Box>
      </Box>
    </HStack>
  );
};

type MobileNavItemProps = NavItem & {
  onLinkClick: () => void;
};

const MobileNavItem = ({
  icon,
  image,
  label,
  children,
  href,
  onLinkClick,
}: MobileNavItemProps) => {
  const {isOpen, onToggle} = useDisclosure();
  const linkColor = useColorModeValue("gray.600", "gray.200");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Stack spacing={4}>
      <Flex py={2} justifyContent="space-between" align="center">
        <HStack spacing={3}>
          {icon && <Icon as={icon} boxSize={6} />}
          {image && <Image src={image} alt={label} width={16} height={16} />}
          <Link href={href ?? "#"} onClick={onLinkClick}>
            <Text fontWeight={600} color={linkColor} fontSize="16px">
              {label}
            </Text>
          </Link>
        </HStack>
        {children && (
          <Icon
            as={BiChevronRight}
            transition="all .25s ease-in-out"
            transform={isOpen ? "rotate(90deg)" : "rotate(0deg)"}
            w={6}
            h={6}
            onClick={onToggle}
          />
        )}
      </Flex>

      {children && (
        <Collapse in={isOpen} animateOpacity>
          <Stack
            mt={2}
            pl={4}
            borderLeft={1}
            borderStyle="solid"
            borderColor={borderColor}
            align="start"
          >
            {children.map((child) => (
              <MobileNavItem key={child.label} {...child} onLinkClick={onLinkClick} />
            ))}
          </Stack>
        </Collapse>
      )}
    </Stack>
  );
};
